import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter as Router, Switch, Route } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import Outsourcers from "./Outsourcers";
import OutsourcerJob from "./OutsourcerJob";
import ClientDatabase from "./ClientDatabasev2";
import Staff from "./Staff/Staff";
import NewStaffForm from "./Staff/NewStaffForm";
import NewStaffFormOld from "./Staff/NewStaffFormOld";
import ImageGrid from "./ClientSelects/ImageGrid";
import SupplyPortal from "./SupplyPortal/SupplyPortal";
import Div100vh from "react-div-100vh";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import RemoteSession from "./RemoteSession";
import {
  db,
  auth,
  functions,
  onSnapshot,
  doc,
  collection,
  where,
  query,
  orderBy,
  updateDoc,
  onAuthStateChanged,
  signInWithCustomToken,
  signOut,
  httpsCallable,
  addDoc,
  serverTimestamp,
} from "./firebaseConfig";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { isDarkMode } from "./Utils/DetectLightDarkMode";
import { useIdleTimer } from "react-idle-timer";
import { useMediaQuery } from "@mui/material";
import { useAtom } from "jotai";
import {
  currentStaffDataAtom,
  userThemeAtom,
  themeAtom,
  userAtom,
  isElectronAtom,
  eventDataModalTabValueAtom,
  listsAtom,
  stylingListsAtom,
  labelsAtom,
  labelColoursAtom,
  showEveryJobAtom,
  staffDataAtom,
  isAdminAtom,
  isSuperAdminAtom,
  isMobileBrowserAtom,
  isMobileAtom,
  jobTypesAtom,
  isDevAtom,
  showWeekendsAtom,
  filterWhenCompletingAtom,
  groupWhenFilteringAtom,
  showDoublesAtom,
  modalAlignmentAtom,
  shouldPauseSubscriptionsAtom,
  sentEmailAddressesAtom,
} from "./-Atoms";
import Layout from "./Layout";
import Login from "./Login";
import Admin from "./Admin";
import Calendar from "./Calendar/Calendar";
import Kanban from "./Kanban/Kanban";
import CustomGrid from "./CustomGrid/CustomGrid.js";
import Dock from "./Dock";
import { getAnalytics } from "firebase/analytics";
import DockFunctions from "./GlobalFunctions/DockFunctions";
import LoginElectron from "./LoginElectron";
import VideoSchedule from "./VideoSchedule.js";
import VersionCheck from "./VersionCheck";
import OperatorChecklistModalV2 from "./GlobalComponents/OperatorChecklistModalV2";
import ImageBreakdownModalV2 from "./GlobalComponents/ImageBreakdownModalV2";
import { browserLocalPersistence, setPersistence } from "firebase/auth";

getAnalytics();

const logErrorToFirestore = async (error, errorInfo, staffData = null) => {
  try {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack || null,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: auth.currentUser?.uid || null,
      userEmail: auth.currentUser?.email || null,
      userDisplayName: auth.currentUser?.displayName || null,
      errorRead: false,
      errorType: error.name || "Error",
      lineNumber: error.lineNumber || null,
      columnNumber: error.columnNumber || null,
      filename: error.filename || null,
      sourceMap: process.env.NODE_ENV === "production" ? "enabled" : "disabled",
    };

    // Add staff data if available
    if (staffData) {
      errorData.staffFirstName = staffData.firstName || null;
      errorData.staffLastName = staffData.lastName || null;
      errorData.staffId = staffData.id || null;
    }

    // Add additional context if available
    if (window.lastUserAction) {
      errorData.lastUserAction = window.lastUserAction;
    }

    await addDoc(collection(db, "ErrorLogs"), errorData);
    console.error("Error logged to Firestore:", error);
  } catch (logError) {
    console.error("Failed to log error to Firestore:", logError);
  }
};

function ErrorFallback({ error, resetErrorBoundary }) {
  const [showDetails, setShowDetails] = useState(false);

  const copyErrorDetails = () => {
    const errorDetails = `
Error: ${error.message}
Stack: ${error.stack}
URL: ${window.location.href}
Time: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
    `.trim();

    navigator.clipboard.writeText(errorDetails).then(() => {
      alert("Error details copied to clipboard");
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        padding: "20px",
        textAlign: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h2>Something went wrong</h2>
      <p style={{ marginBottom: "10px", color: "#666" }}>
        The error has been automatically logged for investigation
      </p>

      <div style={{ marginBottom: "20px" }}>
        <button
          onClick={() => setShowDetails(!showDetails)}
          style={{
            padding: "8px 16px",
            backgroundColor: "#f8f9fa",
            color: "#333",
            border: "1px solid #ddd",
            borderRadius: "4px",
            cursor: "pointer",
            marginRight: "10px",
          }}
        >
          {showDetails ? "Hide Details" : "Show Error Details"}
        </button>

        <button
          onClick={copyErrorDetails}
          style={{
            padding: "8px 16px",
            backgroundColor: "#28a745",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            marginRight: "10px",
          }}
        >
          Copy Error Details
        </button>
      </div>

      {showDetails && (
        <details
          open
          style={{
            whiteSpace: "pre-wrap",
            marginBottom: "20px",
            maxHeight: "40vh",
            overflow: "auto",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "10px",
            backgroundColor: "#f8f9fa",
            fontFamily: "monospace",
            fontSize: "12px",
            textAlign: "left",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          <summary style={{ cursor: "pointer", marginBottom: "10px" }}>
            Error Details
          </summary>
          <strong>Message:</strong> {error.message}
          {error.stack && (
            <>
              <br />
              <br />
              <strong>Stack Trace:</strong>
              <br />
              {error.stack}
            </>
          )}
        </details>
      )}

      <button
        onClick={resetErrorBoundary}
        style={{
          padding: "12px 24px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontSize: "16px",
        }}
      >
        Refresh Page
      </button>
    </div>
  );
}

function isElectronFunc() {
  if (
    typeof window !== "undefined" &&
    typeof window.process === "object" &&
    window.process.type === "renderer"
  ) {
    return true;
  }

  if (
    typeof process !== "undefined" &&
    typeof process.versions === "object" &&
    !!process.versions.electron
  ) {
    return true;
  }

  if (
    typeof navigator === "object" &&
    typeof navigator.userAgent === "string" &&
    navigator.userAgent.indexOf("Electron") >= 0
  ) {
    return true;
  }

  return false;
}

const getSubdomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");

  if (
    parts.length <= 2 ||
    hostname === "localhost" ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname)
  ) {
    return null;
  }

  return parts[0];
};

function App() {
  const subdomain = getSubdomain();
  const [, setEventDataModalTabValue] = useAtom(eventDataModalTabValueAtom);

  const [, setLists] = useAtom(listsAtom);
  const [, setJobTypes] = useAtom(jobTypesAtom);
  const [, setStylingLists] = useAtom(stylingListsAtom);
  const [, setLabels] = useAtom(labelsAtom);
  const [, setLabelColours] = useAtom(labelColoursAtom);
  const [, setSentEmailAddresses] = useAtom(sentEmailAddressesAtom);
  const [, setShowEveryJob] = useAtom(showEveryJobAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [isSuperAdmin] = useAtom(isSuperAdminAtom);
  const [, setIsMobileAtom] = useAtom(isMobileAtom);
  const [isMobileBrowser, setIsMobileBrowserAtom] =
    useAtom(isMobileBrowserAtom);
  const isMobile = useMediaQuery("@media (max-width:780px)");
  const [isElectron, setIsElectron] = useAtom(isElectronAtom);
  const [currentStaffData, setCurrentStaffData] = useAtom(currentStaffDataAtom);
  const [isDev, setIsDev] = useAtom(isDevAtom);
  const [, setShowWeekends] = useAtom(showWeekendsAtom);
  const [, setShowDoubles] = useAtom(showDoublesAtom);
  const [, setModalAlignment] = useAtom(modalAlignmentAtom);
  const [shouldPauseSubscriptions] = useAtom(shouldPauseSubscriptionsAtom);

  const [loadingVisible, setLoadingVisible] = useState(true);
  const [authResolved, setAuthResolved] = useState(false);
  const [coreDataLoaded, setCoreDataLoaded] = useState(false);

  // Fallback timeout to ensure loading doesn't stay forever
  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoadingVisible(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      if (!cancelled) {
        console.warn("Failed to set auth persistence:", error);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setIsDev(process.env.NODE_ENV === "development" ? true : false);
  }, [setIsDev]);

  useEffect(() => {
    setIsMobileBrowserAtom(detectMobileBrowser());
  }, [setIsMobileBrowserAtom]);

  useEffect(() => {
    setEventDataModalTabValue(
      isMobileBrowser || isElectron
        ? isAdmin
          ? currentStaffData?.id === "8jXVYoCymxHLQngklbWE"
            ? 2
            : 0
          : 2
        : 0
    );
  }, [
    isElectron,
    isMobile,
    isMobileBrowser,
    isAdmin,
    currentStaffData,
    setEventDataModalTabValue,
  ]);

  useEffect(() => {
    let unsub = onSnapshot(doc(db, "AdminCollection", "admin"), (doc) => {
      try {
        setJobTypes(doc.data().jobTypes);
      } catch (e) {
        setJobTypes([]);
        console.log(e);
      }
    });
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unsub = onSnapshot(doc(db, "AdminCollection", "lists"), (doc) => {
      try {
        setLists(doc.data().lists);
      } catch (e) {
        setLists([]);
        console.log(e);
      }
    });
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unsub = onSnapshot(
      doc(db, "AdminCollection", "stylingLists"),
      (doc) => {
        try {
          setStylingLists(doc.data().stylingLists);
        } catch (e) {
          console.log(e);
        }
      }
    );
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unsub = onSnapshot(doc(db, "AdminCollection", "labels"), (doc) => {
      try {
        setLabels(doc.data().labels);
        setLabelColours(doc.data().colours);
      } catch (e) {
        console.log(e);
      }
    });
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unsub = onSnapshot(
      doc(db, "AdminCollection", "sentEmailAddresses"),
      (doc) => {
        try {
          setSentEmailAddresses(doc.data().sentEmailAddresses || []);
        } catch (e) {
          setSentEmailAddresses([]);
          console.log(e);
        }
      }
    );
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsMobileAtom(isMobile);
  }, [isMobile, setIsMobileAtom]);

  useEffect(() => {
    setIsElectron(isElectronFunc());
    // eslint-disable-next-line
  }, []);

  const onIdle = () => {};
  const onActive = () => {};
  const onAction = () => {};
  useIdleTimer({
    onIdle,
    onActive,
    onAction,
    timeout: 7200000,
    throttle: 0,
  });

  useEffect(() => {
    let unsub = onSnapshot(doc(db, "-Force Reload", "ForceReload"), (doc) => {
      try {
        const data = doc.data();
        if (data.shouldReload) {
          // window.location.reload();
        }
      } catch (e) {
        console.log(e);
      }
    });
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let unsub = onSnapshot(
      doc(db, "-Force Reload", "ForceReloadNonAdmins"),
      (doc) => {
        try {
          const data = doc.data();
          if (data.shouldReload) {
            if (!isSuperAdmin) {
              if (!isAdmin) {
                // window.location.reload();
              }
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
    );
    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, []);

  const [user, setUser] = useAtom(userAtom);
  const [, setStaffData] = useAtom(staffDataAtom);
  const [, setIsAdmin] = useAtom(isAdminAtom);
  const [, setIsSuperAdmin] = useAtom(isSuperAdminAtom);

  // Hide loading when auth is resolved and core data is loaded
  useEffect(() => {
    if (authResolved && (coreDataLoaded || !user)) {
      setLoadingVisible(false);
    }
  }, [authResolved, coreDataLoaded, user]);

  const isDarkModeValue = isDarkMode();
  const [userTheme, setUserTheme] = useAtom(userThemeAtom);
  const [theme, setTheme] = useAtom(themeAtom);

  const [, setGroupWhenFiltering] = useAtom(groupWhenFilteringAtom);
  const [, setFilterWhenCompleting] = useAtom(filterWhenCompletingAtom);

  const [addPhoneLoginDialog, setAddPhoneLoginDialog] = useState(false);

  const { hideDock } = DockFunctions();
  useEffect(() => {
    if (addPhoneLoginDialog) {
      hideDock();
    }
  }, [hideDock, addPhoneLoginDialog]);

  useEffect(() => {
    const main = () => {
      if (!currentStaffData) return;
      if (!user) return;
      setLoadingVisible(false);
      user.staffData = currentStaffData;

      const hasPhoneProvider = user.providerData.some(
        (obj) => obj.providerId === "phone"
      );
      setAddPhoneLoginDialog(!hasPhoneProvider);

      currentStaffData.hasOwnProperty("filterWhenCompleting")
        ? setFilterWhenCompleting(currentStaffData.filterWhenCompleting)
        : setFilterWhenCompleting(true);

      currentStaffData.hasOwnProperty("groupWhenFiltering")
        ? setGroupWhenFiltering(currentStaffData.groupWhenFiltering)
        : setGroupWhenFiltering(false);
    };
    main();
  }, [currentStaffData, user, setFilterWhenCompleting, setGroupWhenFiltering]);

  useEffect(() => {
    if (!user?.uid || shouldPauseSubscriptions) return;

    const unsub = onSnapshot(
      query(collection(db, "StaffData"), where("uid", "==", user.uid)),
      (snapshot) => {
        if (!snapshot.empty) {
          const userStaffData = {
            ...snapshot.docs[0].data(),
            id: snapshot.docs[0].id,
          };
          localStorage.setItem("uid", user.uid);
          setCurrentStaffData(userStaffData);
          setCoreDataLoaded(true); // Mark core data as loaded
          if (userStaffData.isAdmin || userStaffData.isSuperAdmin) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
          }
          if (userStaffData.isSuperAdmin) {
            setIsSuperAdmin(true);
          } else {
            setIsSuperAdmin(false);
          }
        }
      }
    );

    return () => unsub();
    // eslint-disable-next-line
  }, [user, shouldPauseSubscriptions]);

  useEffect(() => {
    // Don't subscribe when subscriptions should be paused
    if (shouldPauseSubscriptions) {
      return;
    }

    let unsub = onSnapshot(
      query(collection(db, "StaffData"), orderBy("firstName", "asc")),
      (collection) => {
        const sd = collection.docs.map((staff) => {
          const staffDataRaw = staff.data();
          const data = { ...staffDataRaw };
          data.id = staff.id;
          return data;
        });
        setStaffData(sd);
      }
    );

    return () => {
      unsub();
    };
    // eslint-disable-next-line
  }, [shouldPauseSubscriptions]);

  useEffect(() => {
    // Global error handler for unhandled errors
    const globalErrorHandler = (event) => {
      const error = event.error || new Error(event.message);
      logErrorToFirestore(
        error,
        {
          componentStack: "Global error handler",
          type: "unhandled_error",
        },
        currentStaffData
      );
    };

    // Global handler for unhandled promise rejections
    const unhandledRejectionHandler = (event) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));
      logErrorToFirestore(
        error,
        {
          componentStack: "Unhandled promise rejection",
          type: "unhandled_promise_rejection",
        },
        currentStaffData
      );
    };

    // Track user actions for better error context
    const trackUserAction = (event) => {
      window.lastUserAction = {
        type: event.type,
        target: event.target?.tagName || "unknown",
        timestamp: Date.now(),
        url: window.location.href,
      };
    };

    window.addEventListener("error", globalErrorHandler);
    window.addEventListener("unhandledrejection", unhandledRejectionHandler);
    document.addEventListener("click", trackUserAction);
    document.addEventListener("keydown", trackUserAction);

    let isMounted = true;

    auth
      .authStateReady()
      .then(() => {
        if (!isMounted) return;
        setUser(auth.currentUser ?? null);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.warn("authStateReady failed:", error);
      })
      .finally(() => {
        if (!isMounted) return;
        setAuthResolved(true);
      });

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!isMounted) return;
      setUser(user);
      if (user) {
        if (isElectronFunc()) {
          try {
            const createCustomTokenFn = httpsCallable(
              functions,
              "createCustomToken"
            );
            const result = await createCustomTokenFn();

            if (result.data && result.data.customToken) {
              window?.electron?.loginFirebase(result.data.customToken);
            } else {
              console.error("No custom token returned:", result);
            }

            const handleFirebaseLogout = () => {
              signOut(auth).catch((error) => {
                console.error("Error signing out:", error);
              });
            };

            window.electron.onFirebaseLogout(handleFirebaseLogout);
          } catch (error) {
            // console.error("Error getting custom token:", error);
          }
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
      window.removeEventListener("error", globalErrorHandler);
      window.removeEventListener(
        "unhandledrejection",
        unhandledRejectionHandler
      );
      document.removeEventListener("click", trackUserAction);
      document.removeEventListener("keydown", trackUserAction);
    };
    // eslint-disable-next-line
  }, [currentStaffData]);

  useEffect(() => {
    if (user?.staffData?.showEveryJob) {
      setShowEveryJob(user?.staffData?.showEveryJob);
    } else {
      setShowEveryJob(true);
    }
  }, [user, setShowEveryJob]);

  useEffect(() => {
    setTheme(isDarkModeValue ? "dark" : "light");
    // eslint-disable-next-line
  }, [isDarkModeValue]);

  const handleThemeChange = async (newTheme) => {
    if (!newTheme) return;
    if (!user?.staffData?.id) return;
    setUserTheme(newTheme);
    setTheme(
      newTheme === "automatic" ? (isDarkModeValue ? "dark" : "light") : newTheme
    );
    updateDoc(doc(db, "StaffData", user?.staffData?.id), {
      userTheme: newTheme || null,
    });
  };

  useEffect(() => {
    if (currentStaffData.userTheme) {
      setUserTheme(currentStaffData.userTheme);

      setTheme(
        currentStaffData.userTheme === "automatic"
          ? isDarkModeValue
            ? "dark"
            : "light"
          : currentStaffData.userTheme
      );
    } else {
      setUserTheme("automatic");
      setTheme(isDarkModeValue ? "dark" : "light");
    }
    setShowWeekends(currentStaffData.showWeekends);
    setShowDoubles(currentStaffData.showDoubles);
    setModalAlignment(currentStaffData.modalAlignment || "center");
    // eslint-disable-next-line
  }, [isDarkModeValue, currentStaffData]);

  const muiTheme = createTheme({
    palette: {
      mode: theme === "dark" ? "dark" : "light",
      background: {
        default: theme === "dark" ? "#1f1f1f" : "#ffffff",
      },
      text: {
        primary: theme === "dark" ? "#ffffff" : "#000000",
      },
    },
  });

  useEffect(() => {
    const handleGlobalLogout = () => {
      if (user) {
        signOut(auth);
        window?.electron?.logoutFirebase();
      }
    };
    const handleGlobalLogin = (token) => {
      signInWithCustomToken(auth, token)
        .then(() => {})
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
          console.log(errorCode);
          console.log(errorMessage);
        });
    };
    window.handleGlobalLogout = handleGlobalLogout;
    window.handleGlobalLogin = handleGlobalLogin;

    if (isElectron && window.ipcRenderer) {
      window.ipcRenderer?.on("electronLogout", handleGlobalLogout);
      return () => {
        window.ipcRenderer.removeListener("electronLogout", handleGlobalLogout);
      };
    }
  }, [isElectron, user]);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, errorInfo) =>
        logErrorToFirestore(error, errorInfo, currentStaffData)
      }
      onReset={() => window.location.reload()}
    >
      <Div100vh>
        <div id="main-app-container"></div>
        <MuiThemeProvider theme={muiTheme}>
          {/* <VersionCheck /> */}
          <CssBaseline />
          <Router>
            <Switch>
              <Route
                path="/codelogin"
                exact
                render={() => <LoginElectron auth={auth} />}
              />
              {subdomain === "selects" ? (
                <>
                  <Route
                    path="/:jobID/:attestationID"
                    exact
                    render={({ match }) => (
                      <ImageGrid
                        jobID={match.params.jobID}
                        attestationID={match.params.attestationID}
                      />
                    )}
                  />
                  <Route
                    path="/:jobID"
                    exact
                    render={({ match }) => (
                      <ImageGrid jobID={match.params.jobID} />
                    )}
                  />
                </>
              ) : subdomain === "supply" ? (
                <Route
                  path="/:jobID"
                  exact
                  render={({ match }) => (
                    <SupplyPortal jobID={match.params.jobID} />
                  )}
                />
              ) : (
                <Route
                  path="/"
                  exact
                  render={() => (
                    user ? (
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          <>
                            <Dock restricted={user ? false : true} />
                            <Calendar
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          </>
                        }
                      />
                    ) : !isElectron ? (
                      <LoadingComponent
                        Component={authResolved ? <Login auth={auth} /> : null}
                        loadingVisible={loadingVisible || !authResolved}
                        theme={theme}
                        user={user}
                      />
                    ) : (
                      <LoadingComponent
                        Component={authResolved ? <LoginElectron auth={auth} /> : null}
                        loadingVisible={loadingVisible || !authResolved}
                        theme={theme}
                        user={user}
                      />
                    )
                  )}
                />
              )}
              <Route
                path="/clientselects/:jobID/:attestationID"
                exact
                render={({ match }) => (
                  <ImageGrid
                    jobID={match.params.jobID}
                    attestationID={match.params.attestationID}
                  />
                )}
              />
              <Route
                path="/clientselects/:jobID"
                exact
                render={({ match }) => (
                  <ImageGrid jobID={match.params.jobID} />
                )}
              />
              <Route
                path="/supply/:jobID"
                exact
                render={({ match }) => (
                  <SupplyPortal jobID={match.params.jobID} />
                )}
              />
              <Route
                path="/calendar"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          <Calendar
                            user={user}
                            userTheme={userTheme}
                            handleThemeChange={handleThemeChange}
                            theme={theme}
                          />
                        }
                      />
                    </>
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/calendar/autologin"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          <Calendar
                            user={user}
                            userTheme={userTheme}
                            handleThemeChange={handleThemeChange}
                            theme={theme}
                          />
                        }
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/kanban"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={<Kanban user={user} />}
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/kanban/autologin"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={<Kanban user={user} />}
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/videoschedule"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={<VideoSchedule />}
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/shotlist"
                exact
              render={() => (
                user ? (
                  <CustomGrid user={user} />
                ) : (
                  <LoadingComponent
                    Component={
                      authResolved ? <LoginElectron auth={auth} /> : null
                    }
                    loadingVisible={loadingVisible || !authResolved}
                    theme={theme}
                    user={user}
                  />
                )
              )}
            />
              <Route
                path="/clients"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          currentStaffData?.isUtility ? (
                            <Calendar
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          ) : (
                            <ClientDatabase
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          )
                        }
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/staff"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          currentStaffData?.isUtility ? (
                            <Calendar
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          ) : (
                            <Staff
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          )
                        }
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/staff/newstaff/"
                exact
                render={() => <NewStaffForm />}
              />
              <Route
                path="/staff/newstaff/:uid"
                exact
                render={() => (isDev ? <NewStaffForm /> : <NewStaffFormOld />)}
              />
              <Route
                path="/outsourcerjob/:uid"
                exact
                render={() => <OutsourcerJob />}
              />
              <Route
                path="/outsourcers"
                exact
                render={() => (
                  user ? (
                    <LayoutComponent
                      user={user}
                      auth={auth}
                      userTheme={userTheme}
                      handleThemeChange={handleThemeChange}
                      theme={theme}
                      Component={
                        currentStaffData?.isUtility ? (
                          <Calendar
                            user={user}
                            userTheme={userTheme}
                            handleThemeChange={handleThemeChange}
                            theme={theme}
                          />
                        ) : (
                          <>
                            <Dock restricted={user ? false : true} />
                            <Outsourcers
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          </>
                        )
                      }
                    />
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />
              <Route
                path="/admin"
                exact
                render={() => (
                  user ? (
                    <>
                      <Dock restricted={user ? false : true} />
                      <LayoutComponent
                        user={user}
                        auth={auth}
                        userTheme={userTheme}
                        handleThemeChange={handleThemeChange}
                        theme={theme}
                        Component={
                          currentStaffData?.isUtility ? (
                            <Calendar
                              user={user}
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          ) : (
                            <Admin
                              userTheme={userTheme}
                              handleThemeChange={handleThemeChange}
                              theme={theme}
                            />
                          )
                        }
                      />
                    </>
                  ) : !isElectron ? (
                    <LoadingComponent
                      Component={authResolved ? <Login auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  ) : (
                    <LoadingComponent
                      Component={authResolved ? <LoginElectron auth={auth} /> : null}
                      loadingVisible={loadingVisible || !authResolved}
                      theme={theme}
                      user={user}
                    />
                  )
                )}
              />

              <Route
                path="/session/:sessionid"
                exact
                render={() => <RemoteSession theme={theme} />}
              />
            </Switch>
          </Router>

          {/* Global Operator Checklist Modal V2 */}
          <OperatorChecklistModalV2 />

          {/* Global Image Breakdown Modal V2 */}
          <ImageBreakdownModalV2 />
        </MuiThemeProvider>
      </Div100vh>
    </ErrorBoundary>
  );
}

export default App;

const LayoutComponent = ({
  user,
  auth,
  Component,
  search,
  setSearch,
  userTheme,
  handleThemeChange,
  theme,
}) => {
  return (
    <Layout
      user={user}
      auth={auth}
      search={search}
      setSearch={setSearch}
      userTheme={userTheme}
      handleThemeChange={handleThemeChange}
      theme={theme}
    >
      {Component}
    </Layout>
  );
};

const LoadingComponent = ({ Component, loadingVisible, theme, user }) => {
  return (
    <>
      <LoadingFullScreen
        loadingVisible={loadingVisible}
        theme={theme}
        user={user}
      />
      {Component}
    </>
  );
};

// eslint-disable-next-line
function detectMobileBrowser() {
  // eslint-disable-next-line
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  // eslint-disable-next-line
  if (
    // eslint-disable-next-line
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      ua
    ) ||
    // eslint-disable-next-line
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
      ua.substr(0, 4)
    )
  ) {
    return true;
  } else {
    return false;
  }
}

const localStorageCodeKey = "itsConnect.loginHotpCode";

const formatHotpCode = (code) => {
  if (!code) return "";
  const trimmed = code.toString().trim();
  if (trimmed.length === 4) {
    return trimmed.padStart(4, "0");
  }
  if (trimmed.length === 6) {
    const normalized = trimmed.padStart(6, "0");
    return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
  }
  return trimmed;
};

const LoadingFullScreen = ({ loadingVisible, theme, user }) => {
  const [cachedCode, setCachedCode] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const storedCode = window.localStorage.getItem(localStorageCodeKey);
      if (storedCode) {
        setCachedCode(storedCode);
      }
    } catch (error) {
      console.warn("Failed to read cached login code:", error);
    }
  }, [loadingVisible]);

  const loading = {
    box: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      height: "100%",
      gap: 10,
    },
    progress: {
      width: 90,
      height: 90,
    },
  };
  return (
    <div
      style={{
        display: user ? "none" : loadingVisible ? "block" : "none",
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2147483647,
        backgroundColor: theme === "dark" ? "#1f1f1f" : "#f4f5f7",
      }}
    >
      {cachedCode ? (
        <Box
          sx={{
            position: "absolute",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <span
            style={{
              fontWeight: "bold",
              letterSpacing: 6,
              fontSize: 20,
              color: theme === "dark" ? "#ffffff" : "#1f1f1f",
            }}
          >
            {formatHotpCode(cachedCode)}
          </span>
        </Box>
      ) : null}
      <Box sx={loading.box}>
        <CircularProgress style={loading.progress} />
      </Box>
    </div>
  );
};
